import type { ContratoDaObra, SituacaoDeContrato } from '@obras/shared';
import { SITUACOES_DE_CONTRATO } from '@obras/shared';

import { normalizarBusca } from '../../lib/texto.js';

/*
 * O que a planilha de contratos faz na tela: buscar, contar por situação e
 * ordenar. A consulta traz o escopo inteiro, e o resto corre aqui — trocar de
 * aba, digitar e reordenar respondem na hora, sem ida ao servidor.
 */

export type ColunaDaPlanilha = 'obra' | 'contratada' | 'situacao' | 'vigencia' | 'valor' | 'saldo';

export type Sentido = 'asc' | 'desc';

export interface Ordem {
  readonly coluna: ColunaDaPlanilha;
  readonly sentido: Sentido;
}

/** O mais urgente em cima: vencidos, depois o que está mais perto do fim. */
export const ORDEM_PADRAO: Ordem = { coluna: 'situacao', sentido: 'asc' };

/** Valor se ordena do maior para o menor ao primeiro clique; o resto, do começo para o fim. */
export const SENTIDO_INICIAL: Record<ColunaDaPlanilha, Sentido> = {
  obra: 'asc',
  contratada: 'asc',
  situacao: 'asc',
  vigencia: 'asc',
  valor: 'desc',
  saldo: 'desc',
};

export const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha[] = [
  'obra',
  'contratada',
  'situacao',
  'vigencia',
  'valor',
  'saldo',
];

/** Onde a busca procura: o número, a obra, a contratada (e o CNPJ dela) e o fiscal. */
export function textoDeBusca(contrato: ContratoDaObra): string {
  return normalizarBusca(
    [
      contrato.contractNumber ?? '',
      contrato.obraName,
      contrato.company?.name ?? '',
      contrato.company?.cnpj ?? '',
      contrato.responsibleFiscal?.name ?? '',
    ].join(' '),
  );
}

export function contarPorSituacao(
  contratos: readonly ContratoDaObra[],
): Record<SituacaoDeContrato, number> {
  const contagem: Record<SituacaoDeContrato, number> = {
    vencido: 0,
    a_vencer: 0,
    vigente: 0,
    sem_vigencia: 0,
    encerrado: 0,
  };
  for (const contrato of contratos) contagem[contrato.situacao] += 1;
  return contagem;
}

const urgencia = (situacao: SituacaoDeContrato): number => SITUACOES_DE_CONTRATO.indexOf(situacao);

const colador = new Intl.Collator('pt-BR', { numeric: true, sensitivity: 'base' });
const comparaTexto = (a: string, b: string): number => colador.compare(a, b);

/** Compara dois valores da coluna; o que não tem valor fica para o fim, nos dois sentidos. */
function comparaValor(a: string | number | null, b: string | number | null, sinal: number): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  const diferenca =
    typeof a === 'number' && typeof b === 'number' ? a - b : comparaTexto(String(a), String(b));
  return diferenca * sinal;
}

function compara(a: ContratoDaObra, b: ContratoDaObra, ordem: Ordem): number {
  const sinal = ordem.sentido === 'asc' ? 1 : -1;
  switch (ordem.coluna) {
    case 'obra':
      return comparaTexto(a.obraName, b.obraName) * sinal;
    case 'contratada':
      return comparaValor(a.company?.name ?? null, b.company?.name ?? null, sinal);
    case 'situacao':
      // A urgência e, dentro dela, o prazo: o vencido há mais tempo, o que vence antes.
      return (
        (urgencia(a.situacao) - urgencia(b.situacao)) * sinal ||
        comparaValor(a.diasRestantes, b.diasRestantes, sinal)
      );
    case 'vigencia':
      return comparaValor(a.contractEnd, b.contractEnd, sinal);
    case 'valor':
      return (a.currentValueCents - b.currentValueCents) * sinal;
    case 'saldo':
      return (a.saldoCents - b.saldoCents) * sinal;
  }
}

/**
 * A planilha na ordem pedida. **O que não tem valor vai para o fim**, nos dois
 * sentidos — contrato sem data de término no topo da vigência seria ruído —, e
 * o empate se desfaz pela urgência e, depois, pelo nome da obra.
 */
export function ordenar(contratos: readonly ContratoDaObra[], ordem: Ordem): ContratoDaObra[] {
  return [...contratos].sort(
    (a, b) =>
      compara(a, b, ordem) ||
      urgencia(a.situacao) - urgencia(b.situacao) ||
      comparaTexto(a.obraName, b.obraName),
  );
}
